import {
  useContext,
  createContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
} from 'react';
import { Modal, ModalFooter, ModalIcon } from '@sc/shared-ui';
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  LocalStateError,
  ServerError,
  ServerParseError,
  UnconventionalError,
} from '@apollo/client';
import { LinkProps } from '@tanstack/react-router';

type ConfirmModalProps = {
  title: string;
  description: ReactNode;
  onSubmit?: () => void;
  linkProps?: LinkProps;
  closeText?: string;
  icon?: ModalIcon;
  submitText?: string;
  isDelete?: boolean;
  open: boolean;
};

function getErrorMessage(error: unknown) {
  if (CombinedGraphQLErrors.is(error)) {
    return error.message;
  } else if (CombinedProtocolErrors.is(error)) {
    return error.message;
  } else if (LocalStateError.is(error)) {
    return error.message;
  } else if (ServerError.is(error)) {
    return error.message;
  } else if (ServerParseError.is(error)) {
    return error.message;
  } else if (UnconventionalError.is(error)) {
    return error.message;
  } else {
    return 'Unknown error occured';
  }
}

export interface AlertsContext {
  useApolloError: () => (error: unknown) => void;
  setAlertModal: Dispatch<SetStateAction<ConfirmModalProps | undefined>>;
  setResetKey: Dispatch<SetStateAction<number>>;
  resetKey: number;
}

const AlertsContext = createContext<AlertsContext>({} as AlertsContext);

export function AlertsProvider({ children }: { children: ReactNode }) {
  const [alertModal, setAlertModal] = useState<ConfirmModalProps>();

  const useApolloError = useCallback(
    () => (error: unknown) =>
      setAlertModal({
        title: 'Unable to complete request',
        description: getErrorMessage(error),
        icon: 'critical',
        open: true,
      }),
    []
  );

  const [resetKey, setResetKey] = useState(Math.random());

  return (
    <AlertsContext.Provider
      value={{
        setAlertModal,
        useApolloError,
        setResetKey,
        resetKey,
      }}
    >
      <>
        {children}
        <Modal
          open={!!alertModal?.open}
          onClose={() =>
            setAlertModal((al) =>
              al
                ? {
                    ...al,
                    open: false,
                  }
                : undefined
            )
          }
          dialogPanelClassname="max-w-[480px]"
          icon={alertModal?.icon}
          title={alertModal?.title}
          veryDarkDialogBackdrop
        >
          {alertModal && (
            <>
              <div className="pb-3 px-5">
                {typeof alertModal.description === 'string' ? (
                  <p className="text-low-priority">{alertModal.description}</p>
                ) : (
                  alertModal.description
                )}
              </div>
              <ModalFooter
                hideBottomHr
                closeText={alertModal.closeText ?? 'Close'}
                onSubmit={alertModal.onSubmit}
                isDelete={alertModal.isDelete}
                submitText={alertModal.submitText}
                onClose={() =>
                  setAlertModal((al) =>
                    al
                      ? {
                          ...al,
                          open: false,
                        }
                      : undefined
                  )
                }
                linkProps={alertModal.linkProps}
              />
            </>
          )}
        </Modal>
      </>
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  const context = useContext(AlertsContext);

  return context;
}
